/**
 * React Query hooks for Docling SaaS configuration.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  doclingConfigApi,
  type SaveDoclingConfigRequest,
  type TestDoclingKeyRequest,
} from '../../api/docling-config.api';

const doclingConfigKeys = {
  all: ['docling-config'] as const,
  config: () => [...doclingConfigKeys.all, 'config'] as const,
  overrideStatus: () => [...doclingConfigKeys.all, 'override-status'] as const,
};

export function useDoclingConfig() {
  return useQuery({
    queryKey: doclingConfigKeys.config(),
    queryFn: () => doclingConfigApi.getConfig(),
  });
}

export function useSaveDoclingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SaveDoclingConfigRequest) => doclingConfigApi.saveConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: doclingConfigKeys.all });
    },
  });
}

export function useDeleteDoclingConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => doclingConfigApi.deleteConfig(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: doclingConfigKeys.all });
    },
  });
}

export function useTestDoclingKey() {
  return useMutation({
    mutationFn: (data: TestDoclingKeyRequest) => doclingConfigApi.testKey(data),
  });
}

export function useDoclingOverrideStatus() {
  return useQuery({
    queryKey: doclingConfigKeys.overrideStatus(),
    queryFn: () => doclingConfigApi.getOverrideStatus(),
    // Override status rarely changes — cache for 5 min, recheck on window focus
    staleTime: 5 * 60 * 1_000,
  });
}

// Made with Bob
